import { useState } from "react";
import {
  Card, Table, Button, Tag, Select, Popconfirm, message, Tooltip,
} from "antd";
import { RefreshCcw, Cpu, XCircle } from "lucide-react";
import {
  useBackgroundJobList,
  useBackgroundJobCancel,
  useBackgroundJobRetry,
} from "../../../api-hooks/backgroundJobs";
import { TBackgroundJob, TBackgroundJobStatus } from "../../../types";
import type { ColumnsType } from "antd/es/table";

const { Option } = Select;

// Status tag colours — queued=blue, processing=orange, completed=green, failed=red, cancelled=default
const STATUS_COLOR: Record<TBackgroundJobStatus, string> = {
  queued: "blue",
  processing: "orange",
  completed: "green",
  failed: "red",
  cancelled: "default",
};

const QUEUE_OPTIONS = ["all", "default", "emails", "pdf", "sync"];
const STATUS_OPTIONS: Array<"all" | TBackgroundJobStatus> = [
  "all", "queued", "processing", "completed", "failed", "cancelled",
];

export default function BackgroundJobs() {
  const [queue, setQueue] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [messageApi, ctx] = message.useMessage();

  const queryParams = {
    limit: 50,
    ...(queue !== "all" ? { queue } : {}),
    ...(status !== "all" ? { status } : {}),
  };

  const { data, isLoading, refetch, isFetching } = useBackgroundJobList(queryParams);
  const jobs: TBackgroundJob[] = data?.data ?? [];

  const { mutate: cancelJob } = useBackgroundJobCancel({
    onSuccess: () => messageApi.success("Job cancelled."),
    onError: (err: any) => messageApi.error(err?.message || "Failed to cancel job."),
  });

  const { mutate: retryJob } = useBackgroundJobRetry({
    onSuccess: () => messageApi.success("Job re-queued for retry."),
    onError: (err: any) => messageApi.error(err?.message || "Failed to retry job."),
  });

  const columns: ColumnsType<TBackgroundJob> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      render: (val: number) => <span className="text-xs text-slate-400">#{val}</span>,
    },
    {
      title: "Queue",
      dataIndex: "queue",
      key: "queue",
      render: (val: string) => (
        <Tag color="purple" className="font-mono text-xs">{val}</Tag>
      ),
    },
    {
      title: "Job Class",
      dataIndex: "job_class",
      key: "job_class",
      render: (val: string) => (
        // job_class is the server-side class/handler name, e.g. SendInvoiceEmailJob
        <span className="font-mono text-xs text-slate-700">{val}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val: TBackgroundJobStatus) => (
        <Tag color={STATUS_COLOR[val]}>{val}</Tag>
      ),
    },
    {
      title: "Attempts",
      key: "attempts",
      render: (_: any, record: TBackgroundJob) => (
        // attempts / max_attempts — shows how many times this job has been tried
        <span className="text-xs text-slate-500">
          {record.attempts}/{record.max_attempts}
        </span>
      ),
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (val: string | null) => (
        <span className="text-xs text-slate-500">{val || <span className="italic text-slate-300">—</span>}</span>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleString("en-GB")}</span>
      ),
    },
    {
      title: "Failed At",
      dataIndex: "failed_at",
      key: "failed_at",
      render: (val: string | null) =>
        val ? (
          <span className="text-xs text-red-400">{new Date(val).toLocaleString("en-GB")}</span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      title: "Error",
      dataIndex: "error_message",
      key: "error_message",
      render: (val: string | null) =>
        val ? (
          // Show truncated error with full message in tooltip
          <Tooltip title={val} overlayStyle={{ maxWidth: 400 }}>
            <span className="text-xs text-red-500 cursor-help line-clamp-1 max-w-[160px] block">
              {val}
            </span>
          </Tooltip>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: TBackgroundJob) => {
        const canRetry = record.status === "failed" || record.status === "cancelled";
        const canCancel = record.status === "queued" || record.status === "processing";

        return (
          <div className="flex gap-2 justify-end">
            {canRetry && (
              <Popconfirm
                title="Retry this job?"
                description="The job will be re-queued and processed again."
                onConfirm={() => retryJob(record.id)}
                okText="Retry"
                okButtonProps={{ style: { background: "#4c1d95", borderColor: "#4c1d95" } }}
              >
                <Button
                  size="small"
                  icon={<RefreshCcw size={12} />}
                  style={{ color: "#4c1d95", borderColor: "#4c1d95" }}
                >
                  Retry
                </Button>
              </Popconfirm>
            )}
            {canCancel && (
              <Popconfirm
                title="Cancel this job?"
                description="The job will be stopped and marked cancelled. It cannot be undone automatically."
                onConfirm={() => cancelJob(record.id)}
                okText="Cancel Job"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger icon={<XCircle size={12} />}>
                  Cancel
                </Button>
              </Popconfirm>
            )}
            {!canRetry && !canCancel && (
              <span className="text-xs text-slate-300 px-2">—</span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {ctx}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Background Jobs</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Monitor and manage asynchronous job queues — showing last 50 records
          </p>
        </div>
        <Button
          icon={<RefreshCcw size={14} />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          Refresh
        </Button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Queue:</span>
          <Select value={queue} onChange={setQueue} style={{ width: 140 }}>
            {QUEUE_OPTIONS.map((q) => (
              <Option key={q} value={q}>{q === "all" ? "All Queues" : q}</Option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Status:</span>
          <Select value={status} onChange={setStatus} style={{ width: 160 }}>
            {STATUS_OPTIONS.map((s) => (
              <Option key={s} value={s}>{s === "all" ? "All Statuses" : s}</Option>
            ))}
          </Select>
        </div>
        {data?.extra && (
          <span className="text-xs text-slate-400 ml-2">
            {data.extra.total} total
          </span>
        )}
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        {/* No client-side pagination — server returns max 50 records, use filters to narrow down */}
        <Table
          dataSource={jobs}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading || isFetching}
          size="middle"
          scroll={{ x: 1100 }}
          locale={{ emptyText: "No jobs found for the selected filters." }}
          rowClassName={(record) =>
            record.status === "failed" ? "bg-red-50/40" : ""
          }
        />
      </Card>
    </div>
  );
}
